/**
 * AuthService - JWT token management for admin_react
 * Handles token validation, decoding, and storage
 */

const JWT_SECRET = process.env.REACT_APP_JWT_SECRET || 'your_secret_key_change_in_production_2024';

class AuthService {
    /**
     * Decode JWT token (client-side validation)
     * @param {string} token - JWT token
     * @returns {object|null} - Decoded payload or null if invalid
     */
    static decodeToken(token) {
        try {
            // Handle bypass token for development/testing
            if (token === 'pass') {
                return {
                    userId: 'bypass',
                    username: 'bypass_user',
                    bypass: true,
                    exp: Math.floor(Date.now() / 1000) + (24 * 60 * 60) // 24 hours from now
                };
            }

            const parts = token.split('.');
            if (parts.length !== 3) {
                return null;
            }

            const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));

            // Check expiration
            if (payload.exp && payload.exp < Date.now() / 1000) {
                return null;
            }

            return payload;
        } catch (error) {
            console.error('Error decoding token:', error);
            return null;
        }
    }

    /**
     * Save token to localStorage
     * @param {string} token - JWT token
     */
    static saveToken(token) {
        localStorage.setItem('auth_token', token);
    }

    /**
     * Get token from localStorage
     * @returns {string|null} - Token or null
     */
    static getToken() {
        return localStorage.getItem('auth_token');
    }

    /**
     * Remove token from localStorage
     */
    static removeToken() {
        localStorage.removeItem('auth_token');
    }

    /**
     * Check if user is authenticated
     * @returns {boolean}
     */
    static isAuthenticated() {
        const token = this.getToken();
        if (!token) {
            return false;
        }

        const payload = this.decodeToken(token);
        return payload !== null;
    }

    /**
     * Get current user data from token
     * @returns {object|null}
     */
    static getCurrentUser() {
        const token = this.getToken();
        if (!token) {
            return null;
        }

        return this.decodeToken(token);
    }

    /**
     * Logout user
     */
    static logout() {
        this.removeToken();
    }
}

export default AuthService;
